import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Badge, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { getParticipantLayoutAnimatedReactionSrc } from "./participant-layout-animated-reactions";
import { ensureParticipantLayoutReactionSync, PARTICIPANT_LAYOUT_REACTION_EVENT } from "./participant-layout-reactions";

const PARTICIPANT_REACTION_FEED_LIFETIME_MS = 5000;
const PARTICIPANT_REACTION_FEED_MAX_ITEMS = 3;
const PARTICIPANT_REACTION_FEED_LANE_SLOT_COUNT = 7;

const useStyles = makeStyles({
  root: {
    position: "absolute",
    left: "max(16px, env(safe-area-inset-left))",
    bottom: "calc(80px + env(safe-area-inset-bottom))",
    zIndex: 4,
    display: "flex",
    width: "clamp(112px, 16vw, 220px)",
    maxWidth: "calc(100% - 32px)",
    flexDirection: "column",
    alignItems: "stretch",
    gap: tokens.spacingVerticalS,
    pointerEvents: "none",
    "@media screen and (max-width: 599px)": {
      left: "max(12px, env(safe-area-inset-left))",
      bottom: "calc(72px + env(safe-area-inset-bottom))",
      width: "clamp(104px, 34vw, 152px)",
      gap: tokens.spacingVerticalXS
    }
  },
  item: {
    "--participantReactionItemWidth": "96px",
    display: "flex",
    width: "var(--participantReactionItemWidth)",
    flexDirection: "column",
    alignItems: "center",
    pointerEvents: "none",
    animationDuration: "5000ms",
    animationFillMode: "forwards",
    animationTimingFunction: "linear",
    animationName: {
      "0%": {
        transform: "translateY(56px)"
      },
      "100%": {
        transform: "translateY(max(-360px, -42vh))"
      }
    },
    willChange: "transform",
    "@media screen and (max-width: 599px)": {
      "--participantReactionItemWidth": "84px"
    },
    "@media screen and (prefers-reduced-motion: reduce)": {
      animationDuration: "5000ms",
      animationName: {
        "0%": {
          transform: "translateY(0)"
        },
        "100%": {
          transform: "translateY(0)"
        }
      }
    }
  },
  laneSlot0: {
    marginLeft: "0"
  },
  laneSlot1: {
    marginLeft: "calc((100% - var(--participantReactionItemWidth)) * 0.1667)"
  },
  laneSlot2: {
    marginLeft: "calc((100% - var(--participantReactionItemWidth)) * 0.3333)"
  },
  laneSlot3: {
    marginLeft: "calc((100% - var(--participantReactionItemWidth)) * 0.5)"
  },
  laneSlot4: {
    marginLeft: "calc((100% - var(--participantReactionItemWidth)) * 0.6667)"
  },
  laneSlot5: {
    marginLeft: "calc((100% - var(--participantReactionItemWidth)) * 0.8333)"
  },
  laneSlot6: {
    marginLeft: "calc(100% - var(--participantReactionItemWidth))"
  },
  itemContent: {
    display: "flex",
    width: "100%",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalXS,
    animationDuration: "5000ms",
    animationFillMode: "forwards",
    animationTimingFunction: "ease-out",
    animationName: {
      "0%": {
        opacity: 0
      },
      "8%": {
        opacity: 1
      },
      "82%": {
        opacity: 1
      },
      "100%": {
        opacity: 0
      }
    },
    willChange: "opacity"
  },
  emoji: {
    display: "block",
    width: "44px",
    height: "44px",
    objectFit: "contain",
    filter: "drop-shadow(0 10px 16px rgba(0, 0, 0, 0.42))",
    "@media screen and (max-width: 599px)": {
      width: "40px",
      height: "40px"
    }
  },
  label: {
    maxWidth: "96px",
    overflow: "hidden",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "var(--secondaryColorBackground)",
    color: tokens.colorNeutralForegroundInverted,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    boxShadow: "0 8px 18px rgba(0, 0, 0, 0.24)",
    "@media screen and (max-width: 599px)": {
      maxWidth: "84px"
    }
  }
});

function collectParticipants(participants) {
  const participantMap = new Map();

  participants.forEach(participant => {
    participantMap.set(participant.id, participant);

    participant.hiddenParticipants?.forEach(hiddenParticipant => {
      participantMap.set(hiddenParticipant.id, hiddenParticipant);
    });
  });

  return participantMap;
}

function normalizeReaction(detail) {
  if (!detail?.participantId || !detail?.reactionId || !detail?.particleSrc) {
    return null;
  }

  const animatedSrc = getParticipantLayoutAnimatedReactionSrc(detail.emojiId);

  return {
    participantId: detail.participantId,
    reactionId: detail.reactionId,
    emojiId: detail.emojiId || "reaction",
    particleSrc: animatedSrc || detail.particleSrc,
    icon: detail.icon || detail.emojiId || "reaction"
  };
}

function getParticipantReactionLabel(participant) {
  if (participant.isSelf) {
    return "You";
  }

  return participant.displayName;
}

export function getParticipantReactionLaneSlot(reaction, occupiedLaneSlots = []) {
  if (!reaction?.participantId || !reaction?.reactionId) {
    return 0;
  }

  const key = `${reaction.participantId}:${reaction.reactionId}`;
  let hash = 0;

  for (let index = 0; index < key.length; index += 1) {
    hash = (hash * 31 + key.charCodeAt(index)) >>> 0;
  }

  const preferredSlot = hash % PARTICIPANT_REACTION_FEED_LANE_SLOT_COUNT;
  const occupiedSlots = new Set(occupiedLaneSlots);

  for (let offset = 0; offset < PARTICIPANT_REACTION_FEED_LANE_SLOT_COUNT; offset += 1) {
    const slot = (preferredSlot + offset) % PARTICIPANT_REACTION_FEED_LANE_SLOT_COUNT;

    if (!occupiedSlots.has(slot)) {
      return slot;
    }
  }

  return preferredSlot;
}

export function ParticipantReactionFeed({ participants = [] }) {
  const styles = useStyles();
  const participantMap = useMemo(() => collectParticipants(participants), [participants]);
  const participantMapRef = useRef(participantMap);
  const [reactions, setReactions] = useState([]);
  const timeoutIdsRef = useRef([]);

  useEffect(() => {
    participantMapRef.current = participantMap;
  }, [participantMap]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const onReaction = event => {
      const reaction = normalizeReaction(event.detail);
      const participant = reaction ? participantMapRef.current.get(reaction.participantId) : null;

      if (!reaction || !participant) {
        return;
      }

      setReactions(currentReactions => {
        const feedItem = {
          ...reaction,
          label: getParticipantReactionLabel(participant),
          laneSlot: getParticipantReactionLaneSlot(
            reaction,
            currentReactions.map(currentReaction => currentReaction.laneSlot)
          )
        };

        return [...currentReactions, feedItem].slice(-PARTICIPANT_REACTION_FEED_MAX_ITEMS);
      });

      const timeoutId = window.setTimeout(() => {
        setReactions(currentReactions =>
          currentReactions.filter(currentReaction => currentReaction.reactionId !== reaction.reactionId)
        );
      }, PARTICIPANT_REACTION_FEED_LIFETIME_MS);

      timeoutIdsRef.current.push(timeoutId);
    };

    const releaseNetworkReactionSync = ensureParticipantLayoutReactionSync();

    window.addEventListener(PARTICIPANT_LAYOUT_REACTION_EVENT, onReaction);

    return () => {
      window.removeEventListener(PARTICIPANT_LAYOUT_REACTION_EVENT, onReaction);
      releaseNetworkReactionSync();
      timeoutIdsRef.current.forEach(timeoutId => window.clearTimeout(timeoutId));
      timeoutIdsRef.current = [];
    };
  }, []);

  if (reactions.length === 0) {
    return null;
  }

  return (
    <div aria-live="polite" className={styles.root} data-participant-reaction-feed="">
      {reactions.map(reaction => (
        <div
          aria-label={`${reaction.label} reacted with ${reaction.icon}`}
          className={mergeClasses(styles.item, styles[`laneSlot${reaction.laneSlot}`] || styles.laneSlot0)}
          data-participant-reaction-feed-item=""
          data-participant-reaction-emoji-id={reaction.emojiId}
          data-participant-reaction-lane-slot={reaction.laneSlot}
          key={reaction.reactionId}
          role="status"
        >
          <div className={styles.itemContent}>
            <img
              alt=""
              className={styles.emoji}
              data-participant-reaction-feed-emoji=""
              draggable={false}
              src={reaction.particleSrc}
            />
            <Badge
              appearance="filled"
              className={styles.label}
              data-participant-reaction-feed-label=""
              shape="rounded"
              size="small"
            >
              {reaction.label}
            </Badge>
          </div>
        </div>
      ))}
    </div>
  );
}

ParticipantReactionFeed.propTypes = {
  participants: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired,
      hiddenParticipants: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          displayName: PropTypes.string.isRequired,
          isSelf: PropTypes.bool
        })
      ),
      isSelf: PropTypes.bool
    })
  )
};
